<template>
  <v-navigation-drawer
    :value="dialog"
    @input="$emit('update:dialog', $event)"
    fixed
    temporary
    left
    :width="$vuetify.breakpoint.mdAndUp ? '60%' : '100%'"
    class="table-manager-drawer"
    :fullscreen="$vuetify.breakpoint.xsOnly"
  >
    <!-- Header con controles -->
    <v-card>
      <v-card-title>
        <v-toolbar dense flat>
          <v-toolbar-title>
            <v-icon left>mdi-table-chair</v-icon>
            Gestión de Mesas/Cuentas
          </v-toolbar-title>
          <v-spacer></v-spacer>

          <!-- Bot\u00f3n cerrar si est\u00e1 en dialog -->
          <v-btn icon small @click="configDialog = true">
            <v-icon>mdi-cog</v-icon>
          </v-btn>

          <!-- Botones de vista -->
          <v-btn-toggle v-model="viewMode" mandatory dense class="mr-3">
            <v-btn value="grid" small>
              <v-icon small>mdi-view-grid</v-icon>
            </v-btn>
            <v-btn value="list" small>
              <v-icon small>mdi-view-list</v-icon>
            </v-btn>
          </v-btn-toggle>

          <!-- Botón agregar -->
          <v-btn color="primary" small @click="openAddDialog">
            <v-icon left small>mdi-plus</v-icon>
            Agregar
          </v-btn>

          <!-- Botón configuración -->

          <v-btn icon @click="closeDialog" class="mr-2">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
      </v-card-title>
      <!-- Filtros rápidos -->
      <v-card-text class="py-2">
        <v-row dense align="center">
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="searchFilter"
              dense
              outlined
              hide-details
              placeholder="Buscar mesa/cuenta..."
              prepend-inner-icon="mdi-magnify"
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="zoneFilter"
              :items="zones"
              dense
              outlined
              hide-details
              label="Zona"
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              label="Estado"
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-chip-group v-model="typeFilter" column>
              <v-chip filter outlined small value="mesa" color="primary">
                <v-icon left x-small>mdi-table-chair</v-icon>
                Mesas
              </v-chip>
              <v-chip filter outlined small value="cuenta" color="secondary">
                <v-icon left x-small>mdi-account</v-icon>
                Cuentas
              </v-chip>
            </v-chip-group>
          </v-col>
        </v-row>
        <!-- Vista Grid (Drag & Drop) -->
        <div v-if="viewMode === 'grid'" class="grid-container" ref="gridContainer">
          <div
            v-for="table in filteredTables"
            :key="table.id"
            class="table-item"
            :class="{
              'table-occupied': table.status === 'occupied',
              'table-reserved': table.status === 'reserved',
              'table-available': table.status === 'available',
              'table-billing': table.status === 'billing',
              'is-dragging': draggedTable && draggedTable.id === table.id,
            }"
            :style="{
              left: table.position.x + 'px',
              top: table.position.y + 'px',
              width: table.size.width + 'px',
              height: table.size.height + 'px',
            }"
            draggable="true"
            @dragstart="startDrag($event, table)"
            @dragend="endDrag"
            @click="selectTable(table)"
            @contextmenu.prevent="showTableMenu($event, table)"
          >
            <!-- Icono principal -->
            <div class="table-icon">
              <v-icon :color="getTableColor(table)" size="32">
                {{ table.type === "mesa" ? "mdi-table-chair" : "mdi-account-circle" }}
              </v-icon>
            </div>

            <!-- Nombre/Número -->
            <div class="table-name">
              {{ table.name }}
            </div>

            <!-- Info adicional -->
            <div class="table-info">
              <v-chip
                x-small
                :color="getStatusColor(table.status)"
                dark
                v-if="table.status === 'occupied'"
              >
                {{ formatCurrency(table.totalAmount || 0) }}
              </v-chip>
              <span v-else class="text-caption">{{ getStatusText(table.status) }}</span>
            </div>

            <!-- Indicadores -->
            <div class="table-indicators">
              <v-icon v-if="table.hasOrders" x-small color="orange"
                >mdi-clock-outline</v-icon
              >
              <v-icon v-if="table.guests > 0" x-small>mdi-account-multiple</v-icon>
              <span v-if="table.guests > 0" class="text-caption">{{ table.guests }}</span>
            </div>

            <!-- Badge tiempo -->
            <v-badge
              v-if="table.status === 'occupied' && table.startTime"
              :content="getElapsedTime(table.startTime)"
              color="grey darken-2"
              overlap
              top
              right
            />
          </div>

          <!-- Zona de drop para mover mesas -->
          <div class="drop-zone" @dragover.prevent @drop="handleDrop($event)" />
        </div>

        <!-- Vista Lista -->
        <v-card v-else flat>
          <v-data-table
            :headers="tableHeaders"
            :items="filteredTables"
            :search="searchFilter"
            class="elevation-1"
            @click:row="selectTable"
          >
            <template v-slot:[`item.name`]="{ item }">
              <div class="d-flex align-center">
                <v-icon left :color="getTableColor(item)">
                  {{ item.type === "mesa" ? "mdi-table-chair" : "mdi-account-circle" }}
                </v-icon>
                <span class="font-weight-medium">{{ item.name }}</span>
              </div>
            </template>

            <template v-slot:[`item.status`]="{ item }">
              <v-chip small :color="getStatusColor(item.status)" dark>
                {{ getStatusText(item.status) }}
              </v-chip>
            </template>

            <template v-slot:[`item.totalAmount`]="{ item }">
              <span v-if="item.totalAmount > 0">
                {{ formatCurrency(item.totalAmount) }}
              </span>
              <span v-else class="grey--text">-</span>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-btn icon x-small @click.stop="editTable(item)">
                <v-icon x-small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon x-small color="error" @click.stop="deleteTable(item)">
                <v-icon x-small>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-card-text>
    </v-card>

    <!-- Dialog Agregar/Editar -->
    <v-dialog v-model="addEditDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          {{ editingTable ? "Editar" : "Nueva" }}
          {{ currentTable.type === "mesa" ? "Mesa" : "Cuenta" }}
        </v-card-title>
        <v-card-text>
          <v-form ref="tableForm">
            <v-row dense>
              <v-col cols="12">
                <v-radio-group v-model="currentTable.type" row>
                  <v-radio label="Mesa" value="mesa" />
                  <v-radio label="Cuenta Personal" value="cuenta" />
                </v-radio-group>
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="currentTable.name"
                  :label="
                    currentTable.type === 'mesa' ? 'Número/Nombre Mesa' : 'Nombre Cuenta'
                  "
                  outlined
                  dense
                  required
                  :rules="[(v) => !!v || 'Campo requerido']"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-select
                  v-model="currentTable.zone"
                  :items="zones"
                  label="Zona"
                  outlined
                  dense
                />
              </v-col>

              <v-col cols="12" sm="6" v-if="currentTable.type === 'mesa'">
                <v-text-field
                  v-model.number="currentTable.capacity"
                  label="Capacidad"
                  type="number"
                  outlined
                  dense
                  min="1"
                />
              </v-col>

              <v-col cols="12" sm="6" v-if="currentTable.type === 'mesa'">
                <v-select
                  v-model="currentTable.shape"
                  :items="['cuadrada', 'redonda', 'rectangular']"
                  label="Forma"
                  outlined
                  dense
                />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="currentTable.notes"
                  label="Notas"
                  outlined
                  dense
                  rows="2"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeAddEditDialog">Cancelar</v-btn>
          <v-btn color="primary" @click="saveTable">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Configuración -->
    <v-dialog v-model="configDialog" max-width="600px">
      <v-card>
        <v-card-title>Configuración de Zonas</v-card-title>
        <v-card-text>
          <v-list>
            <v-list-item v-for="(zone, idx) in zones" :key="idx">
              <v-list-item-content>
                <v-text-field v-model="zones[idx]" dense outlined hide-details />
              </v-list-item-content>
              <v-list-item-action>
                <v-btn icon small @click="removeZone(idx)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <v-btn color="primary" small block @click="addZone" class="mt-2">
            <v-icon left small>mdi-plus</v-icon>
            Agregar Zona
          </v-btn>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="configDialog = false">Cerrar</v-btn>
          <v-btn color="primary" @click="saveConfig">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Menú contextual -->
    <v-menu
      v-model="contextMenu"
      :position-x="menuX"
      :position-y="menuY"
      absolute
      offset-y
    >
      <v-list dense>
        <v-list-item @click="openPOS(selectedTable)">
          <v-list-item-icon>
            <v-icon small>mdi-cash-register</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Abrir POS</v-list-item-title>
        </v-list-item>

        <v-list-item
          @click="viewOrders(selectedTable)"
          v-if="selectedTable && selectedTable.hasOrders"
        >
          <v-list-item-icon>
            <v-icon small>mdi-receipt</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Ver Comandas</v-list-item-title>
        </v-list-item>

        <v-divider />

        <v-list-item @click="editTable(selectedTable)">
          <v-list-item-icon>
            <v-icon small>mdi-pencil</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Editar</v-list-item-title>
        </v-list-item>

        <v-list-item @click="changeStatus(selectedTable)">
          <v-list-item-icon>
            <v-icon small>mdi-swap-horizontal</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Cambiar Estado</v-list-item-title>
        </v-list-item>

        <v-divider />

        <v-list-item @click="deleteTable(selectedTable)" class="error--text">
          <v-list-item-icon>
            <v-icon small color="error">mdi-delete</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Eliminar</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </v-navigation-drawer>
</template>

<script>
import { formatCurrency } from "@/services/webserver.js";

export default {
  name: "TableManager",
  props: {
    storeId: {
      type: String,
      default: () => window.store && window.store.store_id,
    },
    dialog: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:dialog", "close", "table-selected", "view-orders"],
  data() {
    return {
      viewMode: "grid",
      searchFilter: "",
      zoneFilter: null,
      statusFilter: null,
      typeFilter: null,

      tables: [],
      zones: ["Salón Principal", "Terraza", "Bar", "VIP"],

      statusOptions: [
        { value: "available", text: "Disponible" },
        { value: "occupied", text: "Ocupada" },
        { value: "reserved", text: "Reservada" },
        { value: "billing", text: "Cobrando" },
      ],

      tableHeaders: [
        { text: "Mesa/Cuenta", value: "name" },
        { text: "Zona", value: "zone" },
        { text: "Estado", value: "status" },
        { text: "Personas", value: "guests", align: "center" },
        { text: "Total", value: "totalAmount", align: "right" },
        { text: "Tiempo", value: "elapsedTime" },
        { text: "Acciones", value: "actions", align: "center", sortable: false },
      ],

      addEditDialog: false,
      editingTable: null,
      currentTable: {
        id: null,
        name: "",
        type: "mesa",
        zone: "Salón Principal",
        status: "available",
        capacity: 4,
        shape: "cuadrada",
        guests: 0,
        totalAmount: 0,
        startTime: null,
        hasOrders: false,
        notes: "",
        position: { x: 50, y: 50 },
        size: { width: 100, height: 100 },
      },

      configDialog: false,

      contextMenu: false,
      menuX: 0,
      menuY: 0,
      selectedTable: null,

      draggedTable: null,
    };
  },
  computed: {
    filteredTables() {
      return this.tables.filter((table) => {
        if (
          this.searchFilter &&
          !table.name.toLowerCase().includes(this.searchFilter.toLowerCase())
        ) {
          return false;
        }
        if (this.zoneFilter && table.zone !== this.zoneFilter) {
          return false;
        }
        if (this.statusFilter && table.status !== this.statusFilter) {
          return false;
        }
        if (this.typeFilter && table.type !== this.typeFilter) {
          return false;
        }
        return true;
      });
    },
  },
  mounted() {
    this.loadTables();
    this.loadConfig();
  },
  methods: {
    formatCurrency,

    getEmptyTable() {
      return {
        id: null,
        name: "",
        type: "mesa",
        zone: this.zones && this.zones.length > 0 ? this.zones[0] : "Salón Principal",
        status: "available",
        capacity: 4,
        shape: "cuadrada",
        guests: 0,
        totalAmount: 0,
        startTime: null,
        hasOrders: false,
        notes: "",
        position: { x: 50, y: 50 },
        size: { width: 100, height: 100 },
      };
    },

    loadTables() {
      // Cargar desde localStorage
      const saved = localStorage.getItem(`tables_${this.storeId}`);
      if (saved) {
        this.tables = JSON.parse(saved);
      } else {
        // Crear mesas por defecto
        this.tables = this.createDefaultTables();
        this.saveTables();
      }
    },

    createDefaultTables() {
      const tables = [];
      // Crear 10 mesas por defecto
      for (let i = 1; i <= 10; i++) {
        tables.push({
          ...this.getEmptyTable(),
          id: `mesa_${i}`,
          name: `Mesa ${i}`,
          position: {
            x: 50 + ((i - 1) % 5) * 120,
            y: 50 + Math.floor((i - 1) / 5) * 120,
          },
        });
      }
      return tables;
    },

    saveTables() {
      localStorage.setItem(`tables_${this.storeId}`, JSON.stringify(this.tables));
    },

    loadConfig() {
      const saved = localStorage.getItem(`table_config_${this.storeId}`);
      if (saved) {
        const config = JSON.parse(saved);
        this.zones = config.zones || this.zones;
      }
    },

    saveConfig() {
      localStorage.setItem(
        `table_config_${this.storeId}`,
        JSON.stringify({
          zones: this.zones,
        })
      );
      this.configDialog = false;
    },

    openAddDialog() {
      this.editingTable = null;
      this.currentTable = this.getEmptyTable();
      this.addEditDialog = true;
    },

    editTable(table) {
      this.editingTable = table;
      this.currentTable = { ...table };
      this.addEditDialog = true;
    },

    saveTable() {
      if (!this.$refs.tableForm.validate()) return;

      if (this.editingTable) {
        const index = this.tables.findIndex((t) => t.id === this.editingTable.id);
        if (index > -1) {
          this.tables[index] = { ...this.currentTable };
        }
      } else {
        this.currentTable.id = `${this.currentTable.type}_${Date.now()}`;
        this.tables.push({ ...this.currentTable });
      }

      this.saveTables();
      this.closeAddEditDialog();
    },

    deleteTable(table) {
      if (confirm(`¿Eliminar ${table.name}?`)) {
        const index = this.tables.findIndex((t) => t.id === table.id);
        if (index > -1) {
          this.tables.splice(index, 1);
          this.saveTables();
        }
      }
    },

    closeAddEditDialog() {
      this.addEditDialog = false;
      this.editingTable = null;
      this.currentTable = this.getEmptyTable();
    },
    closeDialog() {
      this.$emit("close");
      this.$emit("update:dialog", false);
    },

    selectTable(table) {
      this.$emit("table-selected", table);
    },

    viewOrders(table) {
      // Ver comandas de la mesa
      this.$emit("view-orders", table);
    },

    changeStatus(table) {
      // Cambiar estado de la mesa
      const currentIndex = this.statusOptions.findIndex((s) => s.value === table.status);
      const nextIndex = (currentIndex + 1) % this.statusOptions.length;
      table.status = this.statusOptions[nextIndex].value;
      this.saveTables();
    },

    // Drag & Drop
    startDrag(event, table) {
      this.draggedTable = table;
      event.dataTransfer.effectAllowed = "move";
    },

    endDrag() {
      this.draggedTable = null;
    },

    handleDrop(event) {
      if (!this.draggedTable) return;

      const rect = this.$refs.gridContainer.getBoundingClientRect();
      const x = event.clientX - rect.left - 50; // Centrar en el cursor
      const y = event.clientY - rect.top - 50;

      const table = this.tables.find((t) => t.id === this.draggedTable.id);
      if (table) {
        table.position = { x: Math.max(0, x), y: Math.max(0, y) };
        this.saveTables();
      }
    },

    showTableMenu(event, table) {
      this.selectedTable = table;
      this.menuX = event.clientX;
      this.menuY = event.clientY;
      this.contextMenu = true;
    },

    // Utilidades
    getTableColor(table) {
      if (table.type === "cuenta") return "secondary";
      switch (table.status) {
        case "occupied":
          return "error";
        case "reserved":
          return "warning";
        case "billing":
          return "orange";
        default:
          return "success";
      }
    },

    getStatusColor(status) {
      switch (status) {
        case "occupied":
          return "error";
        case "reserved":
          return "warning";
        case "billing":
          return "orange";
        default:
          return "success";
      }
    },

    getStatusText(status) {
      const option = this.statusOptions.find((s) => s.value === status);
      return option ? option.text : status;
    },

    getElapsedTime(startTime) {
      if (!startTime) return "";
      const start = new Date(startTime);
      const now = new Date();
      const diff = Math.floor((now - start) / 1000 / 60); // minutos

      if (diff < 60) return `${diff}m`;
      const hours = Math.floor(diff / 60);
      const mins = diff % 60;
      return `${hours}h ${mins}m`;
    },

    addZone() {
      this.zones.push(`Zona ${this.zones.length + 1}`);
    },

    removeZone(index) {
      if (this.zones.length > 1) {
        this.zones.splice(index, 1);
      }
    },
  },
};
</script>

<style scoped>
.table-manager-drawer {
  z-index: 999 !important;
}

.table-manager-container {
  height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
}

.grid-container {
  position: relative;
  flex: 1;
  background: #f5f5f5;
  background-image: linear-gradient(rgba(0, 0, 0, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 0, 0, 0.05) 1px, transparent 1px);
  background-size: 20px 20px;
  overflow: auto;
  min-height: 600px;
}

.drop-zone {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

.table-item {
  position: absolute;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 8px;
  cursor: move;
  transition: all 0.3s;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  user-select: none;
}

.table-item:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transform: scale(1.02);
}

.table-item.is-dragging {
  opacity: 0.5;
  z-index: 100;
}

.table-item.table-available {
  border: 2px solid #4caf50;
}

.table-item.table-occupied {
  border: 2px solid #f44336;
  background: #ffebee;
}

.table-item.table-reserved {
  border: 2px solid #ff9800;
  background: #fff3e0;
}

.table-item.table-billing {
  border: 2px solid #ff6f00;
  background: #ffe0b2;
}

.table-icon {
  margin-bottom: 4px;
}

.table-name {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
}

.table-info {
  font-size: 12px;
  margin-bottom: 4px;
}

.table-indicators {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
}
</style>
